import { Outlet, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Navbar from '../components/layout/Navbar';
import OfflineBanner from '../components/layout/OfflineBanner';
import Footer from '../components/layout/Footer';
import WhatsAppButton from '../components/layout/WhatsAppButton';
import { useEffect } from 'react';
import usePushNotifications from '../hooks/usePushNotifications';
import { ROUTES } from '../constants/routes';

// Home keeps the mint ground; inner pages are white — except the ones the
// reference tints so their white panels lift off it. Measured page by page:
// Reports and Membership are white, these are not.
const TINTED = [
  ROUTES.CART,
  ROUTES.BOOKING,
  ROUTES.DASHBOARD,
  ROUTES.MY_TICKETS,
  ROUTES.TRACK,
];
const siteClass = (pathname) => {
  if (pathname === ROUTES.HOME) return 'tl-site';
  if (TINTED.includes(pathname)) return 'tl-site tl-inner tl-tinted';
  return 'tl-site tl-inner';
};

// Public-facing layout — applied to all non-portal pages
const MainLayout = () => {
  const { pathname, hash } = useLocation();

  // Mount the push-notifications lifecycle once for the whole authed
  // session. Hook is a no-op for guests; for signed-in users with
  // pre-granted permission, it silently re-registers their FCM token.
  usePushNotifications();

  // Scroll behaviour on every URL change:
  //   ─ if the URL has a #hash and an element with that id exists,
  //     smooth-scroll to it (footer "Find a Centre" → #where-we-operate,
  //     About leadership deep-link → #leadership, etc.);
  //   ─ otherwise scroll to the top of the page.
  // Re-runs on hash changes too so hash-only navigations (same path,
  // different anchor) are handled. Two-arg fallback for old Safari which
  // silently ignores `{ behavior: 'instant' }`.
  useEffect(() => {
    if (hash) {
      const id = hash.replace(/^#/, '');
      // Wait one frame so the freshly-mounted target has a layout box.
      requestAnimationFrame(() => {
        const el = id ? document.getElementById(id) : null;
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        window.scrollTo(0, 0);
      });
      return;
    }
    // The theme sets `html { scroll-behavior: smooth }` for the anchor
    // links above, and that applies to this reset too — so every ordinary
    // navigation glided the whole page back to the top instead of landing
    // there. `behavior: 'instant'` is ignored by older Safari, so turn the
    // property off for the jump rather than relying on the argument.
    const de = document.documentElement;
    const prev = de.style.scrollBehavior;
    de.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    de.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
    de.style.scrollBehavior = prev;
  }, [pathname, hash]);

  return (
    // `tl-site` scopes the website's stylesheets. Everything the v5 redesign
    // adds hangs off this class so it can never reach a portal, which renders
    // outside this layout. The standalone website routes (login, signup,
    // app-privacy, app) get the same class from SiteScope in AppRoutes.
    <Box
      className={siteClass(pathname)}
      sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
    >
      {/* One header on every screen, home included. The landing page briefly
          had a cut-down marketing variant from the reference, but it carried
          no account menu, no cart and no notifications — and it showed a
          "Sign in" icon to people who were already signed in. */}
      <Navbar />
      <OfflineBanner />
      <Box component="main" sx={{ flex: 1 }}>
        <Outlet />
      </Box>
      <Footer />
      <WhatsAppButton />
    </Box>
  );
};

export default MainLayout;
